import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sparkles, Quote } from "lucide-react";
import { useI18n, useLocalizedPath } from "../i18n/I18nContext";
import { api } from "../lib/api";
import Icon from "../components/Icon";
import { TID } from "../constants/testIds";

const HERO_IMG = "https://images.pexels.com/photos/30232780/pexels-photo-30232780.jpeg";
const STORY_IMG = "https://images.pexels.com/photos/6037375/pexels-photo-6037375.jpeg";

export default function Home() {
  const { t, lang } = useI18n();
  const lp = useLocalizedPath();
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  }, []);

  const fade = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  };

  return (
    <div className="overflow-x-hidden">
      {/* HERO */}
      <section className="relative pt-12 md:pt-24 pb-24 md:pb-40">
        <div
          className="absolute inset-0 -z-10 opacity-30 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(60% 50% at 50% 0%, rgba(212,175,55,0.18), transparent 70%), url(${HERO_IMG})`,
            backgroundSize: "cover, cover",
            backgroundPosition: "center, center",
            backgroundBlendMode: "screen",
            maskImage: "linear-gradient(180deg, black 0%, black 50%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(180deg, black 0%, black 50%, transparent 100%)",
          }}
        />
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-2 mb-10"
          >
            <span className="font-mono-label text-gold/90 flex items-center gap-2">
              <span className="h-px w-8 bg-gold/40" />
              <Sparkles className="h-3 w-3" />
              {t.home.eyebrow}
            </span>
          </motion.div>

          <h1
            data-testid={TID.heroHeadline}
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tighter leading-[0.95] max-w-5xl"
          >
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="block"
            >
              {t.home.heroLine1}
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
              className="block"
            >
              <span className="ed-italic text-gold">
                {lang === "en" ? "You can rewrite it." : "Îl poți rescrie."}
              </span>
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-10 max-w-xl text-lg text-white/60 leading-relaxed"
          >
            {t.home.heroSub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <Link to={lp("/assessment")} data-testid={TID.heroCtaPrimary} className="btn-gold inline-flex items-center gap-2 group">
              {t.home.ctaPrimary}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to={lp("/guides")} data-testid={TID.heroCtaSecondary} className="btn-ghost inline-flex items-center gap-2">
              {t.home.ctaSecondary}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>

          {/* Stat row */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7 }}
            className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden"
          >
            {[
              { k: "12", l: lang === "en" ? "Practical paths" : "Căi practice" },
              { k: "7", l: lang === "en" ? "Day protocol" : "Zile de protocol" },
              { k: "20m", l: lang === "en" ? "Daily reps" : "Reprize zilnice" },
              { k: "2", l: lang === "en" ? "Languages" : "Limbi" },
            ].map((s, i) => (
              <div key={i} className="bg-obsidian-900 p-6 md:p-8">
                <div className="font-display text-3xl md:text-4xl tracking-tight">{s.k}</div>
                <div className="mt-2 font-mono-label text-white/40">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6">
          <motion.div {...fade} className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16">
            <div className="md:col-span-5">
              <p className="font-mono-label text-gold/80 mb-4">{t.home.howKicker}</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter">
                {t.home.howTitle}
                <span className="ed-italic text-gold">.</span>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 self-end">
              <p className="text-white/55 text-base md:text-lg max-w-md">
                {lang === "en"
                  ? "We strip every guide down to its working parts. No theory dumps, no decade-long retreats. Just the next move, on a page you can finish in twenty minutes."
                  : "Reducem fiecare ghid la părțile sale esențiale. Fără teorii nesfârșite, fără retragerii de un deceniu. Doar următoarea mișcare, pe o pagină pe care o termini în douăzeci de minute."}
              </p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.home.howSteps.map((s, i) => (
              <motion.div
                key={i}
                {...fade}
                transition={{ ...fade.transition, delay: i * 0.07 }}
                data-testid={TID.howStep(i)}
                className="glass glass-hover rounded-2xl p-8 relative overflow-hidden"
              >
                <div className="absolute top-6 right-6 font-mono-label text-white/30">
                  0{i + 1}
                </div>
                <div className="mt-12">
                  <h3 className="font-display text-xl tracking-tight">{s.t}</h3>
                  <p className="mt-3 text-sm text-white/55 leading-relaxed">{s.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="categories" className="py-24 md:py-32 relative">
        <div className="container mx-auto px-6">
          <motion.div {...fade} className="max-w-3xl mb-16">
            <p className="font-mono-label text-gold/80 mb-4">{t.home.categoriesKicker}</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter">
              {t.home.categoriesTitle}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {cats.map((c, i) => (
              <motion.div
                key={c.slug}
                {...fade}
                transition={{ ...fade.transition, delay: (i % 8) * 0.04 }}
              >
                <Link
                  to={lp(`/guides/${c.slug}`)}
                  data-testid={TID.categoryCard(c.slug)}
                  className="glass glass-hover rounded-2xl p-6 md:p-7 block group h-full"
                >
                  <div className="h-10 w-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center mb-6 group-hover:bg-gold/15 transition-colors">
                    <Icon name={c.icon} className="h-5 w-5 text-gold" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-lg tracking-tight">
                    {lang === "en" ? c.name_en : c.name_ro}
                  </h3>
                  <div className="mt-4 flex items-center gap-2 text-xs font-mono-label text-white/40 group-hover:text-gold transition-colors">
                    {lang === "en" ? "Open" : "Deschide"}
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ASSESSMENT CTA */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6">
          <motion.div {...fade} className="glass rounded-3xl overflow-hidden relative">
            <div
              className="absolute inset-0 -z-10 opacity-30"
              style={{
                background:
                  "radial-gradient(60% 100% at 80% 50%, rgba(212,175,55,0.30), transparent 70%)",
              }}
            />
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 p-10 md:p-16">
              <div className="md:col-span-7">
                <p className="font-mono-label text-gold/80 mb-4">
                  {lang === "en" ? "Personal · 3 minutes" : "Personal · 3 minute"}
                </p>
                <h2 className="font-display text-3xl md:text-5xl tracking-tighter leading-tight">
                  {t.home.assessmentTitle}
                </h2>
                <p className="mt-6 max-w-xl text-white/60">{t.home.assessmentSub}</p>
                <Link
                  to={lp("/assessment")}
                  data-testid={TID.assessmentCta}
                  className="btn-gold inline-flex items-center gap-2 mt-10 group"
                >
                  {t.home.assessmentCta}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="md:col-span-5 self-center">
                <div className="space-y-3">
                  {[
                    lang === "en" ? "12 honest questions" : "12 întrebări sincere",
                    lang === "en" ? "AI-powered personal reading" : "Lectură personală generată de AI",
                    lang === "en" ? "3 guides built for you" : "3 ghiduri construite pentru tine",
                  ].map((line, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="h-1 w-1 rounded-full bg-gold" />
                      <span className="text-white/70">{line}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STORY */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <motion.div {...fade} className="md:col-span-6 relative">
              <div
                className="aspect-[4/5] rounded-3xl overflow-hidden"
                style={{
                  backgroundImage: `url(${STORY_IMG})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="absolute -bottom-6 -right-6 glass rounded-2xl p-5 max-w-xs hidden md:block">
                <Quote className="h-5 w-5 text-gold mb-3" />
                <p className="ed-italic text-lg leading-snug text-white/85">
                  {lang === "en"
                    ? '"It is never too late to begin a new chapter."'
                    : '"Nu este niciodată prea târziu să începi un nou capitol."'}
                </p>
              </div>
            </motion.div>

            <motion.div {...fade} className="md:col-span-6 md:pl-8">
              <p className="font-mono-label text-gold/80 mb-4">
                {lang === "en" ? "Why we exist" : "De ce existăm"}
              </p>
              <h2 className="font-display text-4xl md:text-5xl tracking-tighter mb-6">
                {t.home.authorTitle}
              </h2>
              <p className="text-white/65 leading-relaxed text-base md:text-lg">{t.home.authorBody}</p>
              <Link to={lp("/guides")} className="btn-ghost inline-flex items-center gap-2 mt-10">
                {t.home.ctaSecondary}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6">
          <motion.h2 {...fade} className="font-display text-3xl md:text-5xl tracking-tighter mb-16 max-w-3xl">
            {t.home.reviewsTitle}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                q:
                  lang === "en"
                    ? "Read it on a Sunday. By Friday I had a working morning routine for the first time in five years."
                    : "L-am citit duminică. Vineri aveam pentru prima dată o rutină de dimineață funcțională în ultimii cinci ani.",
                n: "Diana M.",
                r: lang === "en" ? "Product designer" : "Designer de produs",
              },
              {
                q:
                  lang === "en"
                    ? "These guides do something most books cannot — they stop and leave you with one move."
                    : "Aceste ghiduri fac ceva ce majoritatea cărților nu pot — se opresc și te lasă cu o singură mișcare.",
                n: "Alex T.",
                r: lang === "en" ? "Founder" : "Antreprenor",
              },
              {
                q:
                  lang === "en"
                    ? "I bought one. I finished it in a weekend. Then I bought five more."
                    : "Am cumpărat unul. L-am terminat într-un weekend. Apoi am cumpărat încă cinci.",
                n: "Vlad R.",
                r: lang === "en" ? "Engineer" : "Inginer",
              },
            ].map((rev, i) => (
              <motion.div
                key={i}
                {...fade}
                transition={{ ...fade.transition, delay: i * 0.08 }}
                className="glass rounded-2xl p-8"
              >
                <Quote className="h-5 w-5 text-gold mb-5" />
                <p className="text-white/85 leading-relaxed">{rev.q}</p>
                <div className="mt-8">
                  <div className="font-display text-sm">{rev.n}</div>
                  <div className="font-mono-label text-white/40">{rev.r}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
